import { AUTOMATION_TRIGGER_OPTIONS } from "@/lib/const";
import { BuilderTrigger } from "@/types/v2/Automation";

/** The catalog id of the "On a schedule" trigger, gated behind isScheduledAutomationsEnabled. */
const SCHEDULE_TRIGGER_ID = "trigger-008";

/**
 * The trigger options the builder's trigger picker should offer.
 *
 * "On a schedule" (WEBV3-77) is fired by its own scheduled scanner in
 * functions-v2, and this repo deploys hosting automatically on merge while
 * functions deploy manually. Offering it before its backend exists lets a
 * shop build an automation that silently never fires — and saved
 * automations are snapshots, so every one built in that window would
 * activate at once when the backend lands.
 *
 * This intentionally does NOT gate the read-only rendering path — a shop
 * that already saved such an automation must still see it render correctly
 * if the flag is later turned off. Callers that just render an
 * already-saved trigger (AutomationDisplay) should keep using
 * AUTOMATION_TRIGGER_OPTIONS directly.
 * @param {object} flags - The shop's relevant feature flags.
 * @param {boolean} flags.isScheduledAutomationsEnabled - "On a schedule" (WEBV3-77).
 * @returns {BuilderTrigger[]} The triggers the picker should show.
 */
export function getVisibleTriggerOptions(flags: {
  isScheduledAutomationsEnabled: boolean;
}): BuilderTrigger[] {
  return AUTOMATION_TRIGGER_OPTIONS.filter((option) => {
    if (option.id === SCHEDULE_TRIGGER_ID) {
      return flags.isScheduledAutomationsEnabled;
    }
    return true;
  });
}

/**
 * The identity key a triggerValueEditable:false condition contributes to
 * contradiction checks. Two conditions sharing this key can only agree if
 * they also carry the same triggerValue — "And is a waiter" (isWaiter,
 * changedTo: null, value: true) and "And is a drop-off" (isWaiter,
 * changedTo: null, value: false) share the key but disagree on value, so
 * selecting or saving both is unsatisfiable under the trigger set's fixed
 * "AND" semantics.
 *
 * Because the key includes `triggerFieldChange`, a change-based fixed
 * trigger (e.g. "trigger-004", "When a card changes to a waiter", key
 * "isWaiter::changedTo") would NOT be caught as contradictory against a
 * state-based sibling on the same field (e.g. a hypothetical null-fieldChange
 * "And is a drop-off" condition, key "isWaiter::null") — even though those
 * are also mutually exclusive in practice. Not reachable today (no such
 * conditional currently exists on trigger-004/trigger-005), but worth
 * knowing for whoever next touches the catalog.
 */
function fixedValueConditionKey(t: {
  triggerField: string | null;
  triggerFieldChange?: string | null;
}): string {
  return `${t.triggerField}::${t.triggerFieldChange ?? "null"}`;
}

/**
 * The conditional triggers a "Select a condition" picker should offer,
 * given what's already selected on this automation.
 *
 * Two rules: never re-offer an already-selected condition (pre-existing
 * behavior), and never offer a triggerValueEditable:false sibling of an
 * already-selected triggerValueEditable:false condition on the same field
 * (WEBV3-216) — once "And is a waiter" is picked, offering "And is a
 * drop-off" would let the user build a trigger set requiring
 * isWaiter === true AND isWaiter === false, which can never be satisfied
 * and would save successfully while silently never firing.
 *
 * Deliberately scoped to triggerValueEditable: false — editable-value
 * fields (container, technician, labor-hours bounds, vehicle make) keep
 * offering every sibling, since combining two of those with different
 * user-chosen values is a legitimate filter, not a built-in impossibility.
 *
 * Note this excludes ANY same-key fixed sibling once one is selected —
 * including one with an identical value, not just a differing one — while
 * hasContradictoryFixedValueConditions (the save-time guard) only flags an
 * actual value disagreement. That's a deliberate, narrower divergence, not
 * an oversight: hiding a redundant duplicate-value pick from the picker is
 * harmless (there's no reason to offer picking the exact same fixed
 * condition twice), and that same-value case doesn't currently exist in the
 * catalog anyway, since each field only has one condition per fixed value.
 * @param {BuilderTrigger[]} conditionalTriggers - The candidate conditions for this picker (one trigger's own `conditionalTriggers` list).
 * @param {BuilderTrigger[]} selectedTriggers - Every trigger already on the automation (the base trigger plus any conditions picked so far).
 * @returns {BuilderTrigger[]} The subset still selectable.
 */
export function getSelectableConditionalTriggers(
  conditionalTriggers: BuilderTrigger[],
  selectedTriggers: BuilderTrigger[]
): BuilderTrigger[] {
  const selectedIds = new Set(selectedTriggers.map((t) => t.id));
  const contradictedKeys = new Set(
    selectedTriggers
      .filter(
        (t) => t.triggerValueEditable === false && t.triggerField !== null
      )
      .map(fixedValueConditionKey)
  );
  return conditionalTriggers.filter((ct) => {
    if (selectedIds.has(ct.id)) return false;
    if (
      ct.triggerValueEditable === false &&
      contradictedKeys.has(fixedValueConditionKey(ct))
    ) {
      return false;
    }
    return true;
  });
}

/**
 * True when the trigger set carries two triggerValueEditable:false
 * conditions on the same field (and field-change) with different fixed
 * values — e.g. "is a waiter" and "is a drop-off" together — which can
 * never both be true at once under the trigger set's "AND" semantics.
 *
 * A save-time backstop, not the primary defense: getSelectableConditionalTriggers
 * (used by AutomationDialog's condition picker) already keeps the builder
 * from constructing this shape, so this should be unreachable through the
 * UI. Mirrors hasOrphanScheduleConditions in automationSchedule.ts — cheap
 * insurance against a future change to how the trigger array is built
 * reintroducing the contradiction (WEBV3-216).
 * @param {object[]} triggers - The trigger set to check.
 * @returns {boolean} Whether a contradictory pair exists.
 */
export function hasContradictoryFixedValueConditions(
  triggers: {
    triggerField: string | null;
    triggerFieldChange?: string | null;
    triggerValueEditable: boolean;
    triggerValue: string | number | boolean | null;
  }[]
): boolean {
  const valuesByKey = new Map<string, Set<string | number | boolean | null>>();
  triggers
    .filter((t) => t.triggerValueEditable === false && t.triggerField !== null)
    .forEach((t) => {
      const key = fixedValueConditionKey(t);
      const values = valuesByKey.get(key) ?? new Set();
      values.add(t.triggerValue);
      valuesByKey.set(key, values);
    });
  return [...valuesByKey.values()].some((values) => values.size > 1);
}
